import { DragDropContext, Droppable, Draggable, type DropResult } from '@hello-pangea/dnd'
import {
  CONFIG_ELEMENTS,
  TestAnswerFeedbackDisplayOption,
  type RankingItem,
} from '@vedalib/editor/lib/elements'
import { rtValueToText } from '@vedalib/rich-text'
import { useUpdateEffect } from 'ahooks'
import { useEffect, useMemo, useRef } from 'react'
import { reorder } from 'utils'

import { buildTestAnnouncement, useLiveFeedback } from 'components/Editor/EditorElements/courseKit/CourseKitUtils'
import TestFeedback from 'components/Editor/EditorElements/courseKit/TestFeedback'
import { useElementRootStyles } from 'components/Editor/EditorElements/hooks/useElementRootStyles'
import { getFeedbackValue } from 'components/Editor/EditorElements/utils/getFeedbackValue'
import ScreenReaderOnlyText from 'components/Editor/styled/ScreenReaderOnlyText'
import { setQuestion } from 'services/Store/Project/actions'
import { PreviewMode } from 'services/Store/Project/enums'
import { useProjectContext, useProjectDispatch } from 'services/Store/Project/hooks'
import { getBlockState } from 'services/Store/Project/selectors'
import { useGetEntityAnalyticsConfig } from 'services/lms/hooks/useGetEntityAnalyticsConfig'
import { notEmpty } from 'utils/notEmpty'
import { shuffle } from 'utils/shuffle'

import type { TestRankingElementType } from '../testRankingTypes'
import * as s from './TestRankingElement.module.scss'
import TestRankingElementPreviewItem from './TestRankingElementPreviewItem'

const TestRankingElementPreview: TestRankingElementType = ({
  element,
  mode,
  styles,
  block,
  state,
  font,
  feedback,
}) => {
  const isPdf = mode.previewMode === PreviewMode.pdf
  const { id: elementId, value } = element
  const dispatch = useProjectDispatch()
  const blockResult = useProjectContext(getBlockState, block?.uuid)?.result
  const wrapperRef = useRef<HTMLDivElement | null>(null)
  const feedbackValue = getFeedbackValue(value)
  const announce = useLiveFeedback('polite')
  const { entityAnalyticsConfig, sendXapiData, baseObjectExtensions } = useGetEntityAnalyticsConfig(
    {
      blockId: block?.uuid || '',
      elementType: CONFIG_ELEMENTS.testRanking.type,
      elementId,
    },
  )
  const hasGeneralFb =
    value.displayFeedback && value.displayOption !== TestAnswerFeedbackDisplayOption.perOption

  const isViewMode = mode.previewMode !== PreviewMode.editor
  const isDisabled = Boolean(blockResult || !isViewMode)
  const rankingInstructionsId = `ranking-instructions-${elementId}`

  // shuffle elements only on first mount(we haven`t state yet) and only in view(preview)
  const valueItems = useMemo<RankingItem[]>(() => {
    if (isViewMode && !isPdf) {
      return (
        state?.value?.map((id) => value.items.find((item) => item.value == id)) ||
        (state?.initialItems as string[])?.map((id) =>
          value.items.find((item) => item.value == id),
        ) ||
        []
      ).filter(notEmpty)
    }

    return value.items
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state, value])

  const onDragEnd = ({ source, destination }: DropResult) => {
    if (!destination || !valueItems.length) {
      announce('Move cancelled')
      return
    }

    const reorderedItems = reorder(valueItems, source.index, destination.index)
    const data = entityAnalyticsConfig.interacted({
      resultExtensions: {
        dragItem: valueItems[source.index]?.value,
        dropItem: valueItems[destination.index]?.value,
        labelDragItem: rtValueToText(valueItems[source.index]?.label),
        labelDropItem: rtValueToText(valueItems[destination.index]?.label),
        dragIndex: source.index,
        dropIndex: destination.index,
      },
      objectExtensions: baseObjectExtensions,
      elementId,
    })
    sendXapiData(data)
    dispatch(
      setQuestion({
        elementId,
        value: {
          value: reorderedItems.map((item) => item.value || ''),
          initialItems: state?.initialItems,
        },
        isReady: true,
        blockId: block?.uuid || '',
      }),
    )
  }

  const rootStyles = useElementRootStyles(styles.root)

  useUpdateEffect(() => {
    if (!blockResult) {
      return
    }
    const correctLabels = value.items.map((item) => rtValueToText(item.label))
    const yourLabels = valueItems.map((item) => rtValueToText(item.label))

    announce(
      buildTestAnnouncement(
        blockResult.isValid,
        correctLabels.length ? correctLabels.join(', ') : undefined,
        yourLabels.length ? yourLabels.join(', ') : undefined,
      ),
    )
  }, [blockResult])

  useEffect(() => {
    if (!state?.initialItems) {
      dispatch(
        setQuestion({
          elementId,
          value: {
            initialItems: shuffle(value.items).map((item) => item.value || ''),
          },
          isReady: false,
          blockId: block?.uuid || '',
        }),
      )
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state?.initialItems])

  return (
    <div ref={wrapperRef} style={rootStyles}>
      <DragDropContext
        onDragEnd={onDragEnd}
        onDragStart={() => announce('Started moving the item')}
      >
        <Droppable
          droppableId='droppable'
          isDropDisabled={isDisabled}
          renderClone={(provided, snapshot, rubric) => (
            <TestRankingElementPreviewItem
              block={block}
              element={element}
              feedback={feedback}
              font={font}
              index={rubric.source.index}
              isDragging={snapshot.isDragging}
              mode={mode}
              provided={provided}
              rankingItem={valueItems[rubric.source.index]}
              styles={styles}
            />
          )}
        >
          {(provided) => (
            <>
              <ScreenReaderOnlyText as='p' id={rankingInstructionsId} hidden>
                Arrange the statements in the correct order. Press space bar to start a drag. When
                dragging you can use the arrow keys to move the item around and escape to cancel.
                Some screen readers may require you to be in focus mode or to use your pass through
                key
              </ScreenReaderOnlyText>
              <ul
                aria-describedby={rankingInstructionsId}
                className={s.items}
                ref={provided.innerRef}
                {...provided.droppableProps}
              >
                {valueItems.map((rankingItem, index) => (
                  <Draggable
                    draggableId={index.toString()}
                    index={index}
                    isDragDisabled={isDisabled}
                    key={index}
                  >
                    {(provided, snapshot) => (
                      <TestRankingElementPreviewItem
                        block={block}
                        element={element}
                        feedback={feedback}
                        font={font}
                        index={index}
                        isDragging={snapshot.isDragging}
                        mode={mode}
                        provided={provided}
                        rankingItem={rankingItem}
                        styles={styles}
                      />
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </ul>
            </>
          )}
        </Droppable>
      </DragDropContext>
      <TestFeedback
        blockUuid={block?.uuid}
        feedbackValue={feedbackValue}
        font={font}
        isTrueAnswer={blockResult?.isValid}
        mode={mode}
        rtProps={{
          value: value.generalFeedback,
        }}
        showFeedback={isPdf ? hasGeneralFb : hasGeneralFb && !!blockResult}
        styles={feedback}
      />
    </div>
  )
}

export default TestRankingElementPreview
